import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, ClipboardList, Notebook, Youtube, Settings, Users, Mail, LogOut, Menu, ExternalLink, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { Toaster } from '@/components/ui/sonner';
import { auth, adminApi } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';

const NAV = [
  { to: '/admin', label: 'Dashboard', Icon: LayoutDashboard, end: true },
  { to: '/admin/season', label: 'Season Entries', Icon: ClipboardList },
  { to: '/admin/field-notes', label: 'Field Notes', Icon: Notebook },
  { to: '/admin/recaps', label: 'Recaps', Icon: Layers },
  { to: '/admin/featured-video', label: 'Featured Video', Icon: Youtube },
  { to: '/admin/site-config', label: 'Site Config', Icon: Settings },
  { to: '/admin/subscribers', label: 'Subscribers', Icon: Users },
  { to: '/admin/inbox', label: 'Contact Inbox', Icon: Mail },
];

const SidebarLinks = ({ onNavigate }) => (
  <nav aria-label="Admin" className="flex flex-col gap-1 px-3">
    {NAV.map(({ to, label, Icon, end }) => (
      <NavLink
        key={to}
        to={to}
        end={end}
        onClick={onNavigate}
        className={({ isActive }) =>
          `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${
            isActive
              ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
              : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--accent)/0.3)] hover:text-[hsl(var(--foreground))]'
          }`
        }
      >
        <Icon size={16} />
        <span>{label}</span>
      </NavLink>
    ))}
  </nav>
);

export const AdminLayout = () => {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    adminApi.me().then(setUser).catch(() => {});
  }, []);

  const logout = () => {
    auth.clear();
    nav('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <header className="sticky top-0 z-30 border-b border-[hsl(var(--border))] bg-[hsl(var(--background)/0.85)] backdrop-blur">
        <div className="flex h-14 items-center gap-3 px-4 sm:px-6">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open admin menu">
                <Menu size={18} />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <SheetTitle className="px-6 pt-6 font-serif text-lg">Admin</SheetTitle>
              <div className="mt-4">
                <SidebarLinks onNavigate={() => setOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>
          <div className="font-serif text-lg tracking-tight">A Guy Who Hunts · Admin</div>
          <div className="ml-auto flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
            >
              View site <ExternalLink size={12} />
            </a>
            <span className="hidden sm:inline text-xs text-[hsl(var(--muted-foreground))]">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={logout} data-testid={ADMIN.logoutButton}>
              <LogOut size={14} className="mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>
      <div className="flex">
        <aside className="hidden lg:block w-64 shrink-0 border-r border-[hsl(var(--border))] py-6">
          <SidebarLinks />
        </aside>
        <main className="flex-1 p-6 sm:p-8">
          <Outlet />
        </main>
      </div>
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
};
